import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Animated, AppState, Linking, StyleSheet, Text, TouchableOpacity, Vibration, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Shadow, Spacing, Typography } from '../../theme';
import { useI18n } from '../../i18n';
import {
  createWaveformData,
  formatAudioDuration,
  getLastRecordingMimeType,
  pauseVoiceRecording,
  resumeVoiceRecording,
  startVoiceRecording,
  stopVoiceRecording,
} from '../../services/ChatAudioService';

interface Props {
  onCancel: () => void;
  onSend: (payload: { uri: string; duration: number; waveformData: number[]; mimeType?: string }) => void;
}

const MAX_RECORDING_MS = 5 * 60 * 1000;

const VoiceRecorderComposer: React.FC<Props> = ({ onCancel, onSend }) => {
  const { t } = useI18n();
  const [recordingUri, setRecordingUri] = useState('');
  const [durationMs, setDurationMs] = useState(0);
  const [meteringValues, setMeteringValues] = useState<number[]>(() => createWaveformData('tonyte-recording', 28));
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const pulse = useRef(new Animated.Value(0)).current;
  const stoppedRef = useRef(false);

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 820, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 820, useNativeDriver: true }),
      ]),
    ).start();
  }, [pulse]);

  useEffect(() => {
    stoppedRef.current = false;
    startVoiceRecording(update => {
      setDurationMs(update.durationMs);
      if (typeof update.metering === 'number') {
        const normalized = Math.max(0.14, Math.min(1, (update.metering + 60) / 60));
        setMeteringValues(prev => [...prev.slice(1), normalized]);
      }
    })
      .then(uri => {
        setRecordingUri(uri);
        Vibration.vibrate(12);
      })
      .catch(error => {
        const message = typeof error?.message === 'string' ? error.message : String(error);
        // Permission is already checked before this component ever mounts
        // (see ChatScreen's handleOpenVoiceRecorder), and on iOS the native
        // recorder itself triggers the real system prompt and rejects with a
        // permission-flavored message if denied. So only show the
        // "access needed" framing when the failure actually looks like a
        // permission problem — anything else (busy audio session, storage
        // issue, transient native error) gets a distinct, honest message
        // instead of falsely blaming a permission that's actually granted.
        const isPermissionError = /permission|denied|not authorized|unauthorized|restricted/i.test(message);
        console.warn('[Tonyte][RN][Voice] start recording failed', { message, isPermissionError });
        if (isPermissionError) {
          Alert.alert(
            t('chat.microphoneAccessNeeded'),
            t('chat.microphoneAccessMessage'),
            [
              { text: t('common.notNow'), style: 'cancel' },
              { text: t('common.openSettings'), onPress: () => Linking.openSettings().catch(() => undefined) },
            ],
          );
        } else {
          Alert.alert(t('chat.recordingUnavailable'), t('chat.recordingUnavailableMessage'));
        }
        onCancel();
      });

    const appStateSub = AppState.addEventListener('change', state => {
      if (state !== 'active') {
        void handleCancel();
      }
    });

    return () => {
      appStateSub.remove();
      if (!stoppedRef.current) {
        stoppedRef.current = true;
        stopVoiceRecording().catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (durationMs >= MAX_RECORDING_MS && !busy) {
      void handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationMs]);

  const waveform = useMemo(() => meteringValues.slice(-28), [meteringValues]);

  const handlePauseResume = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (paused) {
        await resumeVoiceRecording();
        setPaused(false);
      } else {
        await pauseVoiceRecording();
        setPaused(true);
      }
      Vibration.vibrate(8);
    } finally {
      setBusy(false);
    }
  }, [busy, paused]);

  const handleCancel = useCallback(async () => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    setBusy(true);
    try {
      await stopVoiceRecording();
      Vibration.vibrate(8);
    } catch {
      // The recorder may already be stopped by OS interruption.
    } finally {
      onCancel();
    }
  }, [onCancel]);

  const handleSend = useCallback(async () => {
    if (stoppedRef.current || durationMs < 600) {
      await handleCancel();
      return;
    }
    stoppedRef.current = true;
    setBusy(true);
    try {
      const uri = await stopVoiceRecording();
      Vibration.vibrate(16);
      onSend({
        uri: uri || recordingUri,
        duration: durationMs,
        waveformData: waveform,
        mimeType: getLastRecordingMimeType(),
      });
    } catch (error) {
      console.warn('[Tonyte][RN][Voice] stop recording failed', error);
      onCancel();
    }
  }, [durationMs, handleCancel, onCancel, onSend, recordingUri, waveform]);

  const pulseStyle = {
    opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.28, 0.72] }),
    transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.42] }) }],
  };

  return (
    <View style={styles.wrap}>
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(255,255,255,0.095)', 'rgba(255,255,255,0.035)', 'rgba(242,125,38,0.025)']}
        locations={[0, 0.62, 1]}
        style={StyleSheet.absoluteFill}
      />
      <TouchableOpacity style={styles.cancelBtn} activeOpacity={0.84} disabled={busy} onPress={() => void handleCancel()}>
        <Icon name="x" size={18} color="rgba(255,255,255,0.78)" />
      </TouchableOpacity>

      <View style={styles.micWrap}>
        <Animated.View style={[styles.pulse, pulseStyle]} />
        <View style={styles.micDot}>
          <Icon name={paused ? 'pause' : 'mic'} size={16} color="#fff" />
        </View>
      </View>

      <View style={styles.content}>
        <Text style={styles.timer}>{formatAudioDuration(durationMs)}</Text>
        <View style={styles.waveform}>
          {waveform.map((value, index) => (
            <View key={`${index}-${value}`} style={[styles.waveBar, { height: 7 + value * 24 }]} />
          ))}
        </View>
      </View>

      <TouchableOpacity style={styles.pauseBtn} activeOpacity={0.84} disabled={busy} onPress={handlePauseResume}>
        <Icon name={paused ? 'play' : 'pause'} size={17} color={Colors.textPrimary} />
      </TouchableOpacity>
      <TouchableOpacity style={[styles.sendBtn, (busy || durationMs < 600) && styles.sendBtnDisabled]} activeOpacity={0.9} disabled={busy} onPress={() => void handleSend()}>
        <LinearGradient
          pointerEvents="none"
          colors={['#FFA968', Colors.sunset, '#D9640F']}
          start={{ x: 0.15, y: 0 }}
          end={{ x: 0.9, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <Icon name="send" size={17} color="#fff" />
      </TouchableOpacity>
    </View>
  );
};

export default VoiceRecorderComposer;

const styles = StyleSheet.create({
  wrap: {
    marginHorizontal: Spacing.base,
    marginTop: Spacing.sm,
    minHeight: 58,
    borderRadius: 29,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 7,
    backgroundColor: 'rgba(6,10,18,0.72)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    overflow: 'hidden',
    ...Shadow.card,
  },
  cancelBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  micWrap: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  pulse: {
    position: 'absolute',
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: 'rgba(242,125,38,0.35)',
  },
  micDot: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.sunset,
  },
  content: { flex: 1, minWidth: 0 },
  timer: { color: Colors.textPrimary, fontSize: Typography.sm, fontWeight: Typography.extrabold, marginBottom: 4 },
  waveform: { flexDirection: 'row', alignItems: 'center', gap: 2, height: 32 },
  waveBar: { flex: 1, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.70)' },
  pauseBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  sendBtnDisabled: { opacity: 0.55 },
});
